/**
 * OptionInstanceBacktest — the "Backtest" tab for an option-strategy instance.
 * Replays the instance's rules over history (modelled premiums) via
 * POST /option-strategies/backtest and shows an equity curve + stats + trades.
 */
import React, { useState } from 'react';
import {
  Box, Typography, Button, TextField, MenuItem, CircularProgress, ToggleButton, ToggleButtonGroup, Tooltip as MuiTooltip,
  Table, TableHead, TableRow, TableCell, TableBody, useTheme,
} from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import toast from 'react-hot-toast';
import { useAppTheme } from '../../hooks/useAppTheme';
import { optionStrategyService } from '../../services/optionStrategy.service';

const inr = (v: number) => `${v >= 0 ? '+' : '−'}₹${Math.abs(Math.round(v)).toLocaleString('en-IN')}`;
const ymd = (ms: number) => new Date(ms).toISOString().slice(0, 10);

interface Props { strategyId?: number; engine?: string; config?: any; intervalMinutes: number; symbols: string[] }

const OptionInstanceBacktest: React.FC<Props> = ({ strategyId, engine, config, intervalMinutes, symbols }) => {
  const { C } = useAppTheme();
  const theme = useTheme();
  const [symbol, setSymbol] = useState(symbols[0] || 'BTCUSD');
  const [from, setFrom] = useState(ymd(Date.now() - 90 * 864e5));
  const [to, setTo] = useState(ymd(Date.now()));
  const [iv, setIv] = useState(60);
  const [lots, setLots] = useState(1);
  const [mode, setMode] = useState<'model' | 'real'>('model');
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState<any>(null);

  const tip = {
    contentStyle: { backgroundColor: C.chartTooltipBg, border: `1px solid ${C.chartTooltipBorder}`, borderRadius: 6, fontSize: 11 },
    labelStyle: { color: theme.palette.text.primary, fontSize: 11, fontWeight: 700 },
    itemStyle: { color: theme.palette.text.primary, fontSize: 11 },
  };

  const run = async () => {
    if (!symbol) { toast.error('Pick a symbol'); return; }
    const fromMs = Date.parse(`${from}T00:00:00+05:30`);
    const toMs = Date.parse(`${to}T23:59:59+05:30`);
    if (!(fromMs < toMs)) { toast.error('Invalid date range'); return; }
    setLoading(true); setRes(null);
    try {
      const r = await optionStrategyService.backtest({ symbol, intervalMinutes, fromMs, toMs, strategyId, engine, config, ivAnnualPct: iv, lots, pricingMode: mode });
      setRes(r.data?.data || null);
      if (!(r.data?.data?.candleCount)) toast.error(r.data?.data?.note || 'No data in range');
    } catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Backtest failed'); }
    setLoading(false);
  };

  const equity = (res?.equity || []).map((e: any) => ({ t: e.t, equity: e.equity, label: new Date(e.t).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) }));
  const splitOffset = (() => {
    if (!equity.length) return 0.5;
    const mx = Math.max(...equity.map((d: any) => d.equity)), mn = Math.min(...equity.map((d: any) => d.equity));
    if (mx <= 0) return 0; if (mn >= 0) return 1; return mx / (mx - mn);
  })();
  const st = res?.stats;

  const Stat: React.FC<{ label: string; value: React.ReactNode; color?: string }> = ({ label, value, color }) => (
    <Box sx={{ minWidth: 96 }}>
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</Typography>
      <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: color || 'text.primary' }}>{value}</Typography>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
        <TextField select size="small" label="Symbol" value={symbol} onChange={e => setSymbol(e.target.value)} sx={{ minWidth: 130 }}>
          {(symbols.length ? symbols : ['BTCUSD', 'ETHUSD']).map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <TextField type="date" size="small" label="From" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField type="date" size="small" label="To" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField type="number" size="small" label="IV %" value={iv} onChange={e => setIv(Number(e.target.value))} sx={{ width: 90 }} inputProps={{ min: 1, max: 300, step: 5 }} />
        <TextField type="number" size="small" label="Lots" value={lots} onChange={e => setLots(Number(e.target.value))} sx={{ width: 80 }} inputProps={{ min: 1, step: 1 }} />
        <MuiTooltip arrow title={mode === 'real' ? 'Real: actual Delta MARK-price option candles for fills/stops/MTM (slower — fetches each held contract; falls back to the model if a strike is not listed).' : 'Model: Black-Scholes premiums (fast, long history). Validates the decision logic.'}>
          <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_e, v) => v && setMode(v)} sx={{ '& .MuiToggleButton-root': { py: 0.2, px: 1, fontSize: '0.66rem', textTransform: 'none' } }}>
            <ToggleButton value="model">Model</ToggleButton>
            <ToggleButton value="real">Real candles</ToggleButton>
          </ToggleButtonGroup>
        </MuiTooltip>
        <Button variant="contained" onClick={run} disabled={loading}>{loading ? <CircularProgress size={18} /> : 'Run backtest'}</Button>
        <Typography variant="caption" color="text.secondary">Timeframe {intervalMinutes >= 1440 ? '1D' : intervalMinutes >= 60 ? `${intervalMinutes / 60}h` : `${intervalMinutes}m`} · ≤100 days{mode === 'real' ? ' · real mode is slower' : ''}</Typography>
      </Box>

      {!res ? (
        <Typography variant="body2" color="text.secondary">Pick a range and run — replays this instance's rules over history with modelled premiums + costs.</Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1.5 }}>
            <Stat label="Trades" value={st.trades} />
            <Stat label="Win rate" value={`${st.winRatePct.toFixed(0)}%`} color={st.winRatePct >= 50 ? C.bull : C.bear} />
            <Stat label="Net P&L" value={inr(st.netInr)} color={st.netInr >= 0 ? C.bull : C.bear} />
            <Stat label="Gross / Fees" value={`${inr(st.grossInr)} / −₹${Math.round(st.feesInr).toLocaleString('en-IN')}`} />
            <Stat label="Max drawdown" value={`₹${Math.round(st.maxDrawdownInr).toLocaleString('en-IN')}`} color={C.bear} />
            <Stat label="Return" value={`${st.returnPct >= 0 ? '+' : ''}${st.returnPct.toFixed(1)}%`} color={st.returnPct >= 0 ? C.bull : C.bear} />
            <Stat label="Pricing" value={res.pricingMode === 'real' ? `Real${res.modelledTrades ? ` (${res.modelledTrades} modelled)` : ''}` : 'Model'} color={res.pricingMode === 'real' ? C.bull : 'text.secondary'} />
          </Box>

          {equity.length >= 2 && (
            <Box sx={{ height: 220, mb: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={equity} margin={{ top: 6, right: 12, bottom: 4, left: 4 }}>
                  <defs>
                    <linearGradient id="btEq" x1="0" y1="0" x2="0" y2="1">
                      <stop offset={0} stopColor={C.bull} stopOpacity={0.5} />
                      <stop offset={splitOffset} stopColor={C.bull} stopOpacity={0.05} />
                      <stop offset={splitOffset} stopColor={C.bear} stopOpacity={0.05} />
                      <stop offset={1} stopColor={C.bear} stopOpacity={0.5} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: theme.palette.text.secondary }} minTickGap={40} />
                  <YAxis tick={{ fontSize: 10, fill: theme.palette.text.secondary }} width={52} tickFormatter={(v: number) => `₹${Math.round(v)}`} />
                  <Tooltip {...tip} formatter={(v: any) => [`₹${Math.round(Number(v)).toLocaleString('en-IN')}`, 'Equity']} />
                  <ReferenceLine y={0} stroke={C.chartGrid} />
                  <Area type="monotone" dataKey="equity" stroke={st.netInr >= 0 ? C.bull : C.bear} strokeWidth={1.8} fill="url(#btEq)" isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          )}

          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', mb: 0.5 }}>Trades ({res.trades.length})</Typography>
          <Box sx={{ maxHeight: 320, overflow: 'auto', border: `1px solid ${C.chartGrid}`, borderRadius: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead><TableRow>{['Entry', 'Exit', 'Leg', 'Strike', 'Expiry', 'In', 'Out', 'P&L', 'Reason'].map((h, i) => <TableCell key={h} align={['Strike', 'In', 'Out', 'P&L'].includes(h) ? 'right' : 'left'} sx={{ fontWeight: 700, fontSize: '0.64rem', px: 0.8 }}>{h}</TableCell>)}</TableRow></TableHead>
              <TableBody>
                {res.trades.length === 0 && <TableRow><TableCell colSpan={9} sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>No trades in this range.</TableCell></TableRow>}
                {res.trades.slice().reverse().map((t: any, i: number) => (
                  <TableRow key={i}>
                    <TableCell sx={{ fontSize: '0.62rem', px: 0.8, whiteSpace: 'nowrap' }}>{new Date(t.entryAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</TableCell>
                    <TableCell sx={{ fontSize: '0.62rem', px: 0.8, whiteSpace: 'nowrap' }}>{new Date(t.exitAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</TableCell>
                    <TableCell sx={{ fontSize: '0.64rem', px: 0.8, fontWeight: 700, color: t.optType === 'CE' ? C.callText : C.putText }}>{t.optType}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.64rem', px: 0.8 }}>{t.strike.toLocaleString()}</TableCell>
                    <TableCell sx={{ fontSize: '0.62rem', px: 0.8 }}>{t.expiry}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.64rem', px: 0.8 }}>{t.entryPremium}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.64rem', px: 0.8 }}>{t.exitPremium}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.64rem', px: 0.8, fontWeight: 700, color: t.pnlInr >= 0 ? 'success.main' : 'error.main' }}>{inr(t.pnlInr)}</TableCell>
                    <TableCell sx={{ fontSize: '0.62rem', px: 0.8, maxWidth: 260 }}>{t.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{res.note}</Typography>
        </>
      )}
    </Box>
  );
};

export default OptionInstanceBacktest;
